import { isFunction } from './type-utils';

/**
 * Stops an event from happening
 * @param $event
 */
export function stopEvent($event) {
  if (isFunction($event)) {
    return function (ev, ...args) {
      stopEvent(ev);
      return $event(...args);
    };
  }
  if ($event) {
    if ($event.preventDefault) $event.preventDefault();
    $event.returnValue = false;
    stopBubbling($event);
  }
}

/**
 * Stops an event from propagating
 * @param $event
 */
export function stopBubbling($event) {
  if ($event) {
    if ($event.stopPropagation) $event.stopPropagation();
    $event.cancelBubble = true;
  }
}

export const CustomEvent = (function () {
  if (isFunction(window.CustomEvent)) return window.CustomEvent; // For modern browsers

  function CustomEvent(
    event,
    { bubbles = false, cancelable = false, detail } = {},
  ) {
    const evt = document.createEvent('CustomEvent');
    evt.initCustomEvent(event, bubbles, cancelable, detail);
    return evt;
  }

  CustomEvent.prototype = window.Event.prototype;

  return (window.CustomEvent = CustomEvent);
})();

export function isFileEvent(event) {
  const edt = event.dataTransfer || {};
  return (
    (edt.files && edt.files.length) ||
    (edt.items &&
      Array.prototype.some.call(edt.items, (item) => item.kind === 'file'))
  );
}

/**
 * Adds simple event channels to any object (on, off, emit)
 * @param obj
 * @returns obj
 */
export function eventEmitter(obj) {
  let _listeners = {};

  /**
   * Registers an event listener on a named event
   * @param name {string} Name of event
   * @param listener {function} Listener
   * @returns {function} Deregistration function
   */
  function on(name, listener) {
    let namedListeners = _listeners[name];
    if (!namedListeners) {
      _listeners[name] = namedListeners = [];
    }
    namedListeners.push(listener);
    return off.bind(obj, name, listener);
  }

  /**
   * Deregisters an event listener on a named event
   * @param name {string|true} Name of event. If true, destroys all listeners
   * @param listener {function} Listener to deregister
   */
  function off(name, listener) {
    if (name === true) {
      // disable _all_ listeners
      Object.keys(_listeners).forEach((k) => {
        delete _listeners[k];
      });
    } else {
      let namedListeners = _listeners[name];
      if (namedListeners) {
        let indexOfListener = namedListeners.indexOf(listener);
        if (indexOfListener !== -1) {
          namedListeners.splice(indexOfListener, 1);
        }
      }
    }
  }

  /**
   * Emits a named event
   * @param name {string} Name of event
   * @param args {*} Additional arguments to the event
   * @returns {{name: *, preventDefault: (function()), defaultPrevented: boolean}} Resulting event object
   */
  function emit(name, ...args) {
    let namedListeners = _listeners[name];
    let event = {
      name,
      preventDefault() {
        event.defaultPrevented = true;
      },
      defaultPrevented: false,
    };
    if (namedListeners) {
      namedListeners.forEach((l) => isFunction(l) && l(event, ...args));
    }
    return event;
  }

  obj.on = on;
  obj.off = off;
  obj.emit = emit;

  return obj;
}
